import React, { useState } from "react";
import { H2, H3, IconButton, SystemMessage, Text } from "@shoptet/ui";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";
import { CHART_COLORS } from "../../constants/chartColors";
import { GmcIssues } from "../../elements/GmcIssues";
import { ComparisonSummary } from "../../elements/GraphDataSummary/ComparisonSummary";
import { DataSummary } from "../../elements/GraphDataSummary/DataSummary";
import LineChart from "../../elements/LineChart/LineChart";
import { ModalWrapper } from "../../elements/ModalWrapper";
import { PerformanceStatsFilterAdvanced } from "../../elements/PerformanceStats/performanceStatsFilterAdvanced";
import { SmallLoader } from "../../elements/SmallLoader";
import {
  AdSystemT,
  FeatureFlagT,
  MoneyFragmentT,
  PerformanceStatT,
  PerformanceStatsQueryVariablesT,
  useCampaignOptimizationsQuery,
  useCampaignSettingsDetailsQuery,
  useMonthSummaryStatsQuery,
  usePerformanceStatsQuery,
} from "../../graphql/generated/graphql";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useWizardState } from "../../hooks/useWizardState";
import { formatDate } from "../../i18n/formatDate";
import { formatFloat, formatInteger, formatMoney, formatPercents } from "../../i18n/formatNumbers";
import { GraphCompareDataDirectionT, GraphCompareDataT, GraphTab, GraphTabWrapper } from "../../ui/GraphTabs";

export type { MoneyFragmentT };

enum Metrics {
  AVERAGE_CONVERSION_VALUE = "averageConversionValue",
  CLICKS = "clicks",
  CONVERSIONS = "conversions",
  CONVERSION_VALUE = "conversionValue",
  CREDIT = "credit",
  IMPRESSIONS = "impressions",
  PNO = "pno",
}

export enum CampaignTypeFilter {
  ALL = "all",
  GOOGLE = "google",
  META = "meta",
}

const TRANSLATION_KEYS = {
  [Metrics.IMPRESSIONS]: "Metric Impressions",
  [Metrics.CLICKS]: "Metric Clicks",
  [Metrics.CONVERSIONS]: "Metric Conversions",
  [Metrics.CONVERSION_VALUE]: "Metric Conversion Value",
  [Metrics.AVERAGE_CONVERSION_VALUE]: "Metric Average Conversion Value",
  [Metrics.CREDIT]: "Metric Credit",
  [Metrics.PNO]: "Metric PNO",
};

const CHART_INFO_HEADING_KEYS = {
  [Metrics.IMPRESSIONS]: "Chart info Impressions heading",
  [Metrics.CLICKS]: "Chart info Clicks heading",
  [Metrics.CONVERSIONS]: "Chart info Conversions heading",
  [Metrics.CONVERSION_VALUE]: "Chart info Conversion Value heading",
  [Metrics.AVERAGE_CONVERSION_VALUE]: "Chart info Average Conversion Value heading",
  [Metrics.CREDIT]: "Chart info Credit heading",
  [Metrics.PNO]: "Chart info PNO heading",
};

const CHART_INFO_DESCRIPTION_KEYS = {
  [Metrics.IMPRESSIONS]: "Chart info Impressions description",
  [Metrics.CLICKS]: "Chart info Clicks description",
  [Metrics.CONVERSIONS]: "Chart info Conversions description",
  [Metrics.CONVERSION_VALUE]: "Chart info Conversion Value description",
  [Metrics.AVERAGE_CONVERSION_VALUE]: "Chart info Average Conversion Value description",
  [Metrics.CREDIT]: "Chart info Credit description",
  [Metrics.PNO]: "Chart info PNO description",
};

export interface FilterParamsT {
  campaignType?: CampaignTypeFilter;
  comparisonDateFrom?: string;
  comparisonDateTo?: string;
  dateFrom: string;
  dateTo: string;
}

interface ChartDataPointT {
  originalDate?: string;
  x: string;
  y: number | null;
}

interface AlignedChartDataT {
  datasets: {
    backgroundColor: string;
    borderColor: string;
    data: ChartDataPointT[];
    label: string;
    pointBackgroundColor: string;
    pointBorderColor: string;
    pointHoverBackgroundColor: string;
    pointHoverBorderColor: string;
  }[];
  labels: string[];
}

function getDateOffsetDays(dateFrom: string, dateTo: string): number {
  const from = DateTime.fromISO(dateFrom).startOf("day");
  const to = DateTime.fromISO(dateTo).startOf("day");
  return to.diff(from, "days").days;
}

function getDateRange(from: string, to: string): string[] {
  const start = DateTime.fromISO(from).startOf("day");
  const end = DateTime.fromISO(to).startOf("day");

  const result: string[] = [];

  for (let date = start; date <= end; date = date.plus({ days: 1 })) {
    const iso = date.toISODate();
    if (iso) {
      result.push(iso);
    }
  }

  return result;
}

const Statistics = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Stats" });
  const now = DateTime.local();
  const yesterday = now.minus({ days: 1 });

  const [actualRange, setActualRange] = useState<{ dateFrom: string; dateTo: string } | null>({
    dateFrom: now.minus({ days: 30 }).startOf("day").toISO(),
    dateTo: yesterday.endOf("day").toISO(),
  });
  const [compareRange, setCompareRange] = useState<{ dateFrom: string; dateTo: string } | null>(null);
  const [metric, setMetric] = useState<Metrics>(Metrics.IMPRESSIONS);
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<CampaignTypeFilter>(CampaignTypeFilter.ALL);
  const [optimizationModalOpen, setOptimizationModalOpen] = useState(false);

  const { googleWizardFinished, metaWizardFinished } = useWizardState();
  const { enabled: hasMetaCampaignsEnabled } = useFeatureFlag(FeatureFlagT.MetaCampaignsT);

  const { data: googleData, loading } = usePerformanceStatsQuery({
    variables: { ...actualRange, adSystem: AdSystemT.GoogleAdsT } as PerformanceStatsQueryVariablesT,
    fetchPolicy: "no-cache",
    skip: !googleWizardFinished,
  });
  const { data: googleCompareData } = usePerformanceStatsQuery({
    variables: { ...compareRange, adSystem: AdSystemT.GoogleAdsT } as PerformanceStatsQueryVariablesT,
    skip: !compareRange || !googleWizardFinished,
    fetchPolicy: "no-cache",
  });

  const { data: metaData, loading: metaLoading } = usePerformanceStatsQuery({
    variables: { ...actualRange, adSystem: AdSystemT.MetaAdsT } as PerformanceStatsQueryVariablesT,
    fetchPolicy: "no-cache",
    skip: !metaWizardFinished,
  });
  const { data: metaCompareData } = usePerformanceStatsQuery({
    variables: { ...compareRange, adSystem: AdSystemT.MetaAdsT } as PerformanceStatsQueryVariablesT,
    skip: !compareRange || !metaWizardFinished,
    fetchPolicy: "no-cache",
  });

  const { data: googleCampaignOptimizationsData } = useCampaignOptimizationsQuery({
    variables: {
      dateFrom: actualRange?.dateFrom,
      dateTo: actualRange?.dateTo,
      limit: 1000,
      page: 1,
      adSystem: AdSystemT.GoogleAdsT,
    },
    skip: !actualRange || !googleWizardFinished,
    fetchPolicy: "no-cache",
  });

  const { data: metaCampaignOptimizationsData } = useCampaignOptimizationsQuery({
    variables: {
      dateFrom: actualRange?.dateFrom,
      dateTo: actualRange?.dateTo,
      limit: 1000,
      page: 1,
      adSystem: AdSystemT.MetaAdsT,
    },
    skip: !actualRange || !metaWizardFinished,
    fetchPolicy: "no-cache",
  });

  const { data: campaignDetails } = useCampaignSettingsDetailsQuery();

  const { data: googleSummaryStats } = useMonthSummaryStatsQuery({
    variables: {
      thisMonthDateFrom: actualRange?.dateFrom,
      thisMonthDateTo: actualRange?.dateTo,
      lastMonthDateFrom: compareRange?.dateFrom,
      lastMonthDateTo: compareRange?.dateTo,
      adSystem: AdSystemT.GoogleAdsT,
    },
    skip: !googleWizardFinished,
  });

  const { data: metaSummaryStats } = useMonthSummaryStatsQuery({
    variables: {
      thisMonthDateFrom: actualRange?.dateFrom,
      thisMonthDateTo: actualRange?.dateTo,
      lastMonthDateFrom: compareRange?.dateFrom,
      lastMonthDateTo: compareRange?.dateTo,
      adSystem: AdSystemT.MetaAdsT,
    },
    skip: !metaWizardFinished,
  });

  // Combined/filtered summary, computed by the backend (net ÷ net, aggregated across
  // ad systems when adSystem is null). ALL → null, otherwise the single system.
  const summaryAdSystem =
    campaignTypeFilter === CampaignTypeFilter.GOOGLE
      ? AdSystemT.GoogleAdsT
      : campaignTypeFilter === CampaignTypeFilter.META
      ? AdSystemT.MetaAdsT
      : null;

  const { data: summaryStats } = useMonthSummaryStatsQuery({
    variables: {
      thisMonthDateFrom: actualRange?.dateFrom,
      thisMonthDateTo: actualRange?.dateTo,
      lastMonthDateFrom: compareRange?.dateFrom,
      lastMonthDateTo: compareRange?.dateTo,
      adSystem: summaryAdSystem,
    },
    skip: !actualRange || (!googleWizardFinished && !metaWizardFinished),
  });

  const format = (formatMetric: Metrics, value?: null | number | MoneyFragmentT): string => {
    if (typeof value === "number") {
      if (formatMetric === Metrics.PNO) {
        return formatPercents(value * 100);
      }
      if (formatMetric === Metrics.IMPRESSIONS || formatMetric === Metrics.CLICKS) {
        return formatInteger(value);
      }
      return formatFloat(value);
    } else if (typeof value?.amount === "number") {
      return formatMoney(value);
    }
    return "-";
  };

  const formatForGraph = (value?: null | number | MoneyFragmentT, metricType?: Metrics): number => {
    if (metricType === Metrics.PNO) {
      return typeof value === "number" ? value * 100 : 0;
    }
    if (typeof value === "number") {
      return value;
    } else if (typeof value?.amount === "number") {
      return value.amount;
    }
    return 0;
  };

  function getAlignedChartDataWithPeriodShift(
    dateRange: { dateFrom: string; dateTo: string },
    performanceStats: PerformanceStatT[],
    compareStats: PerformanceStatT[],
    unoptimizedStats: PerformanceStatT[],
    metaPerformanceStats: PerformanceStatT[],
    metaCompareStats: PerformanceStatT[],
    metaUnoptimizedStats: PerformanceStatT[],
    _metric: Metrics,
    hasCampaignOptimizations: boolean,
    oldestOptimizationDate: string | null
  ): AlignedChartDataT {
    const actualMap = new Map(performanceStats.map((stat) => [stat.date, stat]));
    const compareMap = new Map(compareStats.map((stat) => [stat.date, stat]));
    const unoptimizedMap = new Map(unoptimizedStats.map((stat) => [stat.date, stat]));

    const metaActualMap = new Map(metaPerformanceStats.map((stat) => [stat.date, stat]));
    const metaCompareMap = new Map(metaCompareStats.map((stat) => [stat.date, stat]));
    const metaUnoptimizedMap = new Map(metaUnoptimizedStats.map((stat) => [stat.date, stat]));

    const days = getDateRange(dateRange.dateFrom, dateRange.dateTo);
    const offsetDays = getDateOffsetDays(dateRange.dateFrom, dateRange.dateTo);

    const labels: string[] = [];
    const actualData: ChartDataPointT[] = [];
    const dataToCompare: ChartDataPointT[] = [];
    const unoptimizedChartData: ChartDataPointT[] = [];
    const metaActualData: ChartDataPointT[] = [];
    const metaDataToCompare: ChartDataPointT[] = [];
    const metaUnoptimizedChartData: ChartDataPointT[] = [];

    for (const dateStr of days) {
      const compareDateStr = DateTime.fromISO(dateStr)
        .minus({ days: offsetDays + 1 })
        .toISODate();

      const actualRow = actualMap.get(dateStr);
      const compareRow = compareMap.get(compareDateStr);
      const unoptimizedRow = unoptimizedMap.get(dateStr);

      const metaActualRow = metaActualMap.get(dateStr);
      const metaCompareRow = metaCompareMap.get(compareDateStr);
      const metaUnoptimizedRow = metaUnoptimizedMap.get(dateStr);

      const label = formatDate(dateStr);
      labels.push(label);

      const actualValue = actualRow ? formatForGraph(actualRow[_metric], _metric) : 0;
      const compareValue = compareRow ? formatForGraph(compareRow[_metric], _metric) : 0;

      const metaActualValue = metaActualRow ? formatForGraph(metaActualRow[_metric], _metric) : 0;
      const metaCompareValue = metaCompareRow ? formatForGraph(metaCompareRow[_metric], _metric) : 0;

      // Map metric to unoptimized field
      const unoptimizedMetricMap: Record<string, keyof PerformanceStatT> = {
        [Metrics.CLICKS]: "unoptimizedClicks",
        [Metrics.CONVERSION_VALUE]: "unoptimizedConversionValue",
        [Metrics.CONVERSIONS]: "unoptimizedConversions",
        [Metrics.IMPRESSIONS]: "unoptimizedImpressions",
      };
      const unoptimizedField = unoptimizedMetricMap[_metric];

      // Only include unoptimized data from the oldest optimization date onwards
      const shouldIncludeUnoptimizedData =
        oldestOptimizationDate && DateTime.fromISO(dateStr) >= DateTime.fromISO(oldestOptimizationDate);

      const unoptimizedValue =
        unoptimizedRow && unoptimizedField && shouldIncludeUnoptimizedData
          ? formatForGraph(unoptimizedRow[unoptimizedField] as number | null | MoneyFragmentT, _metric)
          : null;

      const metaUnoptimizedValue =
        metaUnoptimizedRow && unoptimizedField && shouldIncludeUnoptimizedData
          ? formatForGraph(metaUnoptimizedRow[unoptimizedField] as number | null | MoneyFragmentT, _metric)
          : null;

      actualData.push({ x: label, y: actualValue });
      dataToCompare.push({
        x: label,
        y: compareValue,
        originalDate: compareRow ? compareRow.date : undefined,
      });
      unoptimizedChartData.push({ x: label, y: unoptimizedValue });

      metaActualData.push({ x: label, y: metaActualValue });
      metaDataToCompare.push({
        x: label,
        y: metaCompareValue,
        originalDate: metaCompareRow ? metaCompareRow.date : undefined,
      });
      metaUnoptimizedChartData.push({ x: label, y: metaUnoptimizedValue });
    }

    const hasActualData = actualData.some((point) => point.y !== 0);
    const hasCompareData = dataToCompare.some((point) => point.y !== 0);
    const hasMetaActualData = metaActualData.some((point) => point.y !== 0);
    const hasMetaCompareData = metaDataToCompare.some((point) => point.y !== 0);
    // Only consider having unoptimized data if:
    // 1. There's an oldest optimization date
    // 2. The metric supports unoptimized data (has a field mapping)
    // 3. There's at least one non-null and non-zero value in the filtered data
    const unoptimizedMetricMap: Record<string, keyof PerformanceStatT> = {
      [Metrics.CLICKS]: "unoptimizedClicks",
      [Metrics.CONVERSION_VALUE]: "unoptimizedConversionValue",
      [Metrics.CONVERSIONS]: "unoptimizedConversions",
      [Metrics.IMPRESSIONS]: "unoptimizedImpressions",
    };
    const hasUnoptimizedData =
      oldestOptimizationDate !== null &&
      unoptimizedMetricMap[_metric] !== undefined &&
      unoptimizedChartData.some((point) => point.y !== null && point.y !== 0);
    const hasMetaUnoptimizedData =
      oldestOptimizationDate !== null &&
      unoptimizedMetricMap[_metric] !== undefined &&
      metaUnoptimizedChartData.some((point) => point.y !== null && point.y !== 0);

    const datasets = [];

    const formatDateCompact = (date: string) => {
      return formatDate(date).replace(/\s/g, "");
    };

    const actualLabel = `${t("Google Actual data")} (${formatDateCompact(dateRange.dateFrom)} - ${formatDateCompact(
      dateRange.dateTo
    )})`;
    const compareLabel =
      compareStats.length > 0
        ? `${t("Google Compare data")} (${formatDateCompact(compareStats[0].date)} - ${formatDateCompact(
            compareStats[compareStats.length - 1].date
          )})`
        : t("Google Compare data");

    const metaActualLabel = `${t("Meta Actual data")} (${formatDateCompact(dateRange.dateFrom)} - ${formatDateCompact(
      dateRange.dateTo
    )})`;
    const metaCompareLabel =
      metaCompareStats.length > 0
        ? `${t("Meta Compare data")} (${formatDateCompact(metaCompareStats[0].date)} - ${formatDateCompact(
            metaCompareStats[metaCompareStats.length - 1].date
          )})`
        : t("Meta Compare data");

    // Google actual data
    if (hasActualData) {
      datasets.push({
        label: actualLabel,
        data: actualData,
        borderColor: CHART_COLORS.google,
        backgroundColor: "rgba(0,0,0,0)",
        pointBackgroundColor: CHART_COLORS.google,
        pointBorderColor: "rgba(255,255,255, 1)",
        pointHoverBackgroundColor: CHART_COLORS.google,
        pointHoverBorderColor: "rgba(255,255,255, 1)",
      });
    }

    // Google compare data
    if (hasCompareData && compareStats.length > 0) {
      datasets.push({
        label: compareLabel,
        data: dataToCompare,
        borderColor: CHART_COLORS.googleCompare,
        backgroundColor: "rgba(0,0,0,0)",
        pointBackgroundColor: CHART_COLORS.googleCompare,
        pointBorderColor: "rgba(255,255,255, 1)",
        pointHoverBackgroundColor: CHART_COLORS.googleCompare,
        pointHoverBorderColor: "rgba(255,255,255, 1)",
      });
    }

    // Meta actual data
    if (hasMetaActualData) {
      datasets.push({
        label: metaActualLabel,
        data: metaActualData,
        borderColor: CHART_COLORS.meta,
        backgroundColor: "rgba(0,0,0,0)",
        pointBackgroundColor: CHART_COLORS.meta,
        pointBorderColor: "rgba(255,255,255, 1)",
        pointHoverBackgroundColor: CHART_COLORS.meta,
        pointHoverBorderColor: "rgba(255,255,255, 1)",
      });
    }

    // Meta compare data
    if (hasMetaCompareData && metaCompareStats.length > 0) {
      datasets.push({
        label: metaCompareLabel,
        data: metaDataToCompare,
        borderColor: CHART_COLORS.metaCompare,
        backgroundColor: "rgba(0,0,0,0)",
        pointBackgroundColor: CHART_COLORS.metaCompare,
        pointBorderColor: "rgba(255,255,255, 1)",
        pointHoverBackgroundColor: CHART_COLORS.metaCompare,
        pointHoverBorderColor: "rgba(255,255,255, 1)",
      });
    }

    // Google unoptimized data (at the end)
    if (hasUnoptimizedData && compareStats.length === 0 && unoptimizedStats.length > 0 && hasCampaignOptimizations) {
      datasets.push({
        label: t("Google unoptimized data"),
        data: unoptimizedChartData,
        borderColor: CHART_COLORS.unoptimized,
        backgroundColor: CHART_COLORS.unoptimized,
        pointBackgroundColor: CHART_COLORS.unoptimized,
        pointBorderColor: "rgba(0,0,0,0)",
        pointHoverBackgroundColor: "rgba(0,0,0,0)",
        pointHoverBorderColor: "rgba(0,0,0,0)",
        pointRadius: 0,
        pointHoverRadius: 0,
        borderDash: [5, 5],
        showLine: true,
      });
    }

    // Meta unoptimized data (at the end)
    if (
      hasMetaUnoptimizedData &&
      metaCompareStats.length === 0 &&
      metaUnoptimizedStats.length > 0 &&
      hasCampaignOptimizations
    ) {
      datasets.push({
        label: t("Meta unoptimized data"),
        data: metaUnoptimizedChartData,
        borderColor: CHART_COLORS.unoptimized,
        backgroundColor: CHART_COLORS.unoptimized,
        pointBackgroundColor: CHART_COLORS.unoptimized,
        pointBorderColor: "rgba(0,0,0,0)",
        pointHoverBackgroundColor: "rgba(0,0,0,0)",
        pointHoverBorderColor: "rgba(0,0,0,0)",
        pointRadius: 0,
        pointHoverRadius: 0,
        borderDash: [5, 5],
        showLine: true,
      });
    }

    return {
      labels,
      datasets,
    };
  }

  const handleFilter = (newDateRange: FilterParamsT) => {
    setActualRange({ dateFrom: newDateRange.dateFrom, dateTo: newDateRange.dateTo });

    if (newDateRange.campaignType) {
      setCampaignTypeFilter(newDateRange.campaignType);
    }

    if (newDateRange.comparisonDateFrom && newDateRange.comparisonDateTo) {
      setCompareRange({ dateFrom: newDateRange.comparisonDateFrom, dateTo: newDateRange.comparisonDateTo });
    }
  };

  const googlePerformanceStats =
    (googleData?.organization?.performanceStats?.length || 0) > 0 ? googleData?.organization?.performanceStats : null;
  const googleCompareStats =
    (googleCompareData?.organization?.performanceStats?.length || 0) > 0
      ? googleCompareData?.organization?.performanceStats
      : null;
  // Unoptimized data comes from the same query as performanceStats
  const googleUnoptimizedStats = googlePerformanceStats;

  const metaPerformanceStats =
    (metaData?.organization?.performanceStats?.length || 0) > 0 ? metaData?.organization?.performanceStats : null;
  const metaCompareStats =
    (metaCompareData?.organization?.performanceStats?.length || 0) > 0
      ? metaCompareData?.organization?.performanceStats
      : null;
  const metaUnoptimizedStats = metaPerformanceStats;

  const handleCompareClear = () => {
    setCompareRange(null);
  };

  // Filter data based on campaign type filter
  const includeGoogle =
    campaignTypeFilter === CampaignTypeFilter.ALL || campaignTypeFilter === CampaignTypeFilter.GOOGLE;
  const includeMeta = campaignTypeFilter === CampaignTypeFilter.ALL || campaignTypeFilter === CampaignTypeFilter.META;

  // Combine Google and Meta campaign optimizations based on campaign type filter
  const allCampaignOptimizations = [
    ...(includeGoogle ? googleCampaignOptimizationsData?.organization?.campaignOptimizations || [] : []),
    ...(includeMeta ? metaCampaignOptimizationsData?.organization?.campaignOptimizations || [] : []),
  ];

  const hasCampaignOptimizations = allCampaignOptimizations.length > 0;

  // Find the oldest optimization date based on filtered campaign type
  const oldestOptimizationDate =
    allCampaignOptimizations.length > 0
      ? allCampaignOptimizations
          .map((opt) => DateTime.fromISO(opt.createdAt))
          .sort((a, b) => a.toMillis() - b.toMillis())[0]
          .toISODate()
      : null;

  const alignedChartData = actualRange
    ? getAlignedChartDataWithPeriodShift(
        actualRange,
        includeGoogle ? googlePerformanceStats || [] : [],
        includeGoogle ? googleCompareStats || [] : [],
        includeGoogle ? googleUnoptimizedStats || [] : [],
        includeMeta ? metaPerformanceStats || [] : [],
        includeMeta ? metaCompareStats || [] : [],
        includeMeta ? metaUnoptimizedStats || [] : [],
        metric,
        hasCampaignOptimizations,
        oldestOptimizationDate
      )
    : { labels: [], datasets: [] };

  // Extract and format campaign optimization dates for vertical lines from both Google and Meta
  const optimizationDates =
    allCampaignOptimizations
      .map((opt) => {
        const dateStr = DateTime.fromISO(opt.createdAt).toISODate();
        return dateStr ? formatDate(dateStr) : null;
      })
      .filter((date): date is string => date !== null) || [];

  // Helper function to combine Google and Meta values (filtered by campaign type)
  const getCombinedValue = (metricType: Metrics, period: "thisMonth" | "lastMonth"): number | MoneyFragmentT => {
    const statsKey = period === "thisMonth" ? "thisMonthStats" : "lastMonthStats";

    const googleValue = includeGoogle ? googleSummaryStats?.organization?.[statsKey]?.[metricType] : undefined;
    const metaValue = includeMeta ? metaSummaryStats?.organization?.[statsKey]?.[metricType] : undefined;

    const monetaryMetrics = [Metrics.CONVERSION_VALUE, Metrics.AVERAGE_CONVERSION_VALUE, Metrics.CREDIT];
    const isMonetaryMetric = monetaryMetrics.includes(metricType);

    if (metricType === Metrics.PNO) {
      // Combined PNO comes from the backend (net ÷ net, aggregated across ad systems) — no FE math.
      return summaryStats?.organization?.[statsKey]?.pno || 0;
    } else if (metricType === Metrics.AVERAGE_CONVERSION_VALUE) {
      const combined = summaryStats?.organization?.[statsKey]?.averageConversionValue as MoneyFragmentT | undefined;
      return {
        amount: combined?.amount || 0,
        currency:
          combined?.currency ||
          (googleValue as MoneyFragmentT)?.currency ||
          (metaValue as MoneyFragmentT)?.currency ||
          "CZK",
      } as MoneyFragmentT;
    } else if (isMonetaryMetric) {
      const googleAmount = (googleValue as MoneyFragmentT)?.amount || 0;
      const metaAmount = (metaValue as MoneyFragmentT)?.amount || 0;
      const currency = (googleValue as MoneyFragmentT)?.currency || (metaValue as MoneyFragmentT)?.currency || "CZK";
      return {
        amount: googleAmount + metaAmount,
        currency,
      } as MoneyFragmentT;
    } else {
      return ((googleValue as number) || 0) + ((metaValue as number) || 0);
    }
  };

  const getCombinedTabCompareData = (metricType: Metrics): GraphCompareDataT | null => {
    if (!googleCompareData && !metaCompareData) {
      return null;
    }

    const thisMonthValue = getCombinedValue(metricType, "thisMonth");
    const lastMonthValue = getCombinedValue(metricType, "lastMonth");

    const monetaryMetrics = [Metrics.CONVERSION_VALUE, Metrics.AVERAGE_CONVERSION_VALUE, Metrics.CREDIT];
    const isMonetaryMetric = monetaryMetrics.includes(metricType);

    const thisAmount = isMonetaryMetric ? (thisMonthValue as MoneyFragmentT)?.amount : (thisMonthValue as number);
    const lastAmount = isMonetaryMetric ? (lastMonthValue as MoneyFragmentT)?.amount : (lastMonthValue as number);

    let percentageValue = 0;

    if (thisAmount && lastAmount) {
      if (lastAmount && lastAmount !== 0) {
        percentageValue = (thisAmount - lastAmount) / lastAmount;
      } else if (thisAmount && thisAmount > 0) {
        percentageValue = 1;
      } else if (thisAmount === 0 && lastAmount && lastAmount > 0) {
        percentageValue = -1;
      }
    }

    const direction = percentageValue >= 0 ? GraphCompareDataDirectionT.Positive : GraphCompareDataDirectionT.Negative;

    return {
      direction: direction,
      percentageValue: `${(percentageValue * 100).toFixed(2)}%`,
      value: format(metricType, lastMonthValue),
    };
  };
  return (
    <div>
      <H2>{t("Headline")}</H2>
      <H3>{t("Stats Summary Headline")}</H3>
      <div className="performance-stats__chart">
        <PerformanceStatsFilterAdvanced
          campaignStartDate={campaignDetails?.organization?.startedAt}
          googleWizardFinished={googleWizardFinished}
          metaWizardFinished={metaWizardFinished}
          onCompareClear={handleCompareClear}
          onFilterChange={(filter: FilterParamsT) => handleFilter(filter)}
        />

        <GraphTabWrapper tabsCount={7}>
          <GraphTab
            active={metric === Metrics.IMPRESSIONS}
            compareData={getCombinedTabCompareData(Metrics.IMPRESSIONS)}
            header={t(TRANSLATION_KEYS[Metrics.IMPRESSIONS])}
            main={format(Metrics.IMPRESSIONS, getCombinedValue(Metrics.IMPRESSIONS, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.IMPRESSIONS)}
          />
          <GraphTab
            active={metric === Metrics.CLICKS}
            compareData={getCombinedTabCompareData(Metrics.CLICKS)}
            header={t(TRANSLATION_KEYS[Metrics.CLICKS])}
            main={format(Metrics.CLICKS, getCombinedValue(Metrics.CLICKS, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.CLICKS)}
          />
          <GraphTab
            active={metric === Metrics.CONVERSIONS}
            compareData={getCombinedTabCompareData(Metrics.CONVERSIONS)}
            header={t(TRANSLATION_KEYS[Metrics.CONVERSIONS])}
            main={format(Metrics.CONVERSIONS, getCombinedValue(Metrics.CONVERSIONS, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.CONVERSIONS)}
          />
          <GraphTab
            active={metric === Metrics.CONVERSION_VALUE}
            compareData={getCombinedTabCompareData(Metrics.CONVERSION_VALUE)}
            header={t(TRANSLATION_KEYS[Metrics.CONVERSION_VALUE])}
            main={format(Metrics.CONVERSION_VALUE, getCombinedValue(Metrics.CONVERSION_VALUE, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.CONVERSION_VALUE)}
          />
          <GraphTab
            active={metric === Metrics.AVERAGE_CONVERSION_VALUE}
            compareData={getCombinedTabCompareData(Metrics.AVERAGE_CONVERSION_VALUE)}
            header={t(TRANSLATION_KEYS[Metrics.AVERAGE_CONVERSION_VALUE])}
            main={
              format(
                Metrics.AVERAGE_CONVERSION_VALUE,
                getCombinedValue(Metrics.AVERAGE_CONVERSION_VALUE, "thisMonth")
              ) || "-"
            }
            onClick={() => setMetric(Metrics.AVERAGE_CONVERSION_VALUE)}
          />
          <GraphTab
            active={metric === Metrics.CREDIT}
            compareData={getCombinedTabCompareData(Metrics.CREDIT)}
            header={t(TRANSLATION_KEYS[Metrics.CREDIT])}
            main={format(Metrics.CREDIT, getCombinedValue(Metrics.CREDIT, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.CREDIT)}
          />
          <GraphTab
            active={metric === Metrics.PNO}
            compareData={getCombinedTabCompareData(Metrics.PNO)}
            header={t(TRANSLATION_KEYS[Metrics.PNO])}
            invertArrowLogic={true}
            main={format(Metrics.PNO, getCombinedValue(Metrics.PNO, "thisMonth")) || "-"}
            onClick={() => setMetric(Metrics.PNO)}
          />
        </GraphTabWrapper>

        <div
          style={{
            marginTop: "40px",
            marginBottom: "32px",
          }}
        >
          <SystemMessage
            level="notice"
            title={t(CHART_INFO_HEADING_KEYS[metric])}
            description={
              <Trans
                components={{ a: <a style={{ color: "var(--app-link)" }} target="blank" /> }}
                i18nKey={CHART_INFO_DESCRIPTION_KEYS[metric]}
                t={t as never}
              />
            }
          />
        </div>

        {compareRange ? (
          <ComparisonSummary
            campaignTypeFilter={campaignTypeFilter}
            combinedCompareValueRaw={getCombinedValue(metric, "lastMonth")}
            combinedCurrentValueRaw={getCombinedValue(metric, "thisMonth")}
            googleCompareValue={format(metric, googleSummaryStats?.organization?.lastMonthStats?.[metric])}
            googleCurrentValue={format(metric, googleSummaryStats?.organization?.thisMonthStats?.[metric])}
            hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false}
            invertArrowLogic={metric === Metrics.PNO}
            metaCompareValue={format(metric, metaSummaryStats?.organization?.lastMonthStats?.[metric])}
            metaCurrentValue={format(metric, metaSummaryStats?.organization?.thisMonthStats?.[metric])}
            metaWizardFinished={metaWizardFinished}
            metric={metric}
            metricLabel={t(TRANSLATION_KEYS[metric])}
          />
        ) : googleWizardFinished && metaWizardFinished ? (
          <DataSummary
            campaignTypeFilter={campaignTypeFilter}
            googleValue={format(metric, googleSummaryStats?.organization?.thisMonthStats?.[metric])}
            hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false}
            metaValue={format(metric, metaSummaryStats?.organization?.thisMonthStats?.[metric])}
            metaWizardFinished={metaWizardFinished}
            metric={metric}
            metricLabel={t(TRANSLATION_KEYS[metric])}
          />
        ) : null}

        {(googleWizardFinished && loading) || (metaWizardFinished && metaLoading) ? (
          <SmallLoader />
        ) : (googleWizardFinished && googleData?.organization?.performanceStats) ||
          (metaWizardFinished && metaData?.organization?.performanceStats) ? (
          <LineChart
            data={alignedChartData}
            height={300}
            optimizationDates={optimizationDates}
            optimizationLabel={t("When optimization happened")}
            optimizationLegendContent={
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span>{t("When optimization happened")}</span>
                <IconButton
                  icon="shp-info"
                  ghost
                  onClick={(event) => {
                    event.stopPropagation();
                    setOptimizationModalOpen(true);
                  }}
                />
              </div>
            }
          />
        ) : null}
      </div>
      <div id="product-recommendations">
        <h2>{t("Recommendation Headline")}</h2>
        <GmcIssues />
      </div>
      <ModalWrapper
        isOpen={optimizationModalOpen}
        title={t("Optimization modal title")}
        maxWidth
        onClose={() => setOptimizationModalOpen(false)}
      >
        <Text>
          <Trans
            components={{ a: <a style={{ color: "var(--app-link)" }} target="_blank" />, b: <b /> }}
            i18nKey="Optimization modal text"
            t={t}
          />
        </Text>
      </ModalWrapper>
    </div>
  );
};

export default Statistics;
